import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { useEffect } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import * as CONST from "../utils/Constants";
import { LoaderComponent } from "../components/Loader/LoaderComponent";
import CmsPageOverride from "../components/CmsPageOverride/CmsPageOverride";

// Eager (not React.lazy(), unlike every other route component in this
// file): these are exactly the routes server/page-ssr.js and server/seo.js
// now server-render with the real component tree (see
// src/ssr/serverRender.js) instead of the generic placeholder - and that
// server-side render imports each of them directly (never lazy - see
// serverRender.js's own comment on why: renderToString doesn't wait on
// React.lazy/Suspense). Keeping them lazy here too would risk a real
// hydration mismatch: if hydrateRoot() runs before a lazy chunk has
// resolved, React renders that lazy component's Suspense fallback
// (LoaderComponent, per the route config below) instead of reconciling
// against the real server-rendered HTML - closer to a full remount of the
// page than true hydration. Eager, top-level imports guarantee these are
// already resolved - part of the main bundle, not a separate chunk - by the
// time index.js's hydrateRoot call runs, so client and server always agree
// on what's actually rendered here. Every other route is untouched: still
// React.lazy(), still code-split, still served by the placeholder SSR
// system exactly as before.
import HomePage from "../pages/HomePage/HomePage";
import About from "../screens/About/About";
import LandingScreen from "../screens/LandingScreen/LandingScreen";
import BuildingBetterCourtsDetail from "../screens/Research/BuildingBetterCourtsDetail";

import SpacingPlanning from "../screens/JaldiLab/Chapters/SpacingPlanning";
import CourtRoomsPage from "../screens/JaldiLab/Chapters/CourtRooms";
import WaitingAreas from "../screens/JaldiLab/Chapters/WaitingAreas";
import VWDC from "../screens/JaldiLab/Chapters/VWDC";
import DesignPrincipals from "../screens/JaldiLab/Chapters/DesignPrincipals";
import IntroductionPage from "../screens/JaldiLab/Chapters/Introduction";
import MappingUserPage from "../screens/JaldiLab/Chapters/Mappingusers";
import JudgeChamber from "../screens/JaldiLab/Chapters/JudgeChamber";
import FacilitiesForJudges from "../screens/JaldiLab/Chapters/FacilitiesForJudges";
import AdministrativeWing from "../screens/JaldiLab/Chapters/AdministrativeWings";
import FilingSection from "../screens/JaldiLab/Chapters/FilingSection";
import OfficeSpacesPage from "../screens/JaldiLab/Chapters/OfficeSpaces";
import RecordsOffices from "../screens/JaldiLab/Chapters/RecordsOffices";
import StaffLounge from "../screens/JaldiLab/Chapters/StaffLounge";
import AdvocatesBarAssociation from "../screens/JaldiLab/Chapters/AdvocatesBarAssociation";
import ChamberAssistant from "../screens/JaldiLab/Chapters/ChamberAssistant";
import LockUp from "../screens/JaldiLab/Chapters/JudicialLockUp";
import VisitationRoom from "../screens/JaldiLab/Chapters/VisitationRoom";
import PoliceInspectorRoom from "../screens/JaldiLab/Chapters/PoliceInspectorRoom";
import ESevaKendra from "../screens/JaldiLab/Chapters/ESevaKendra";
import LegalService from "../screens/JaldiLab/Chapters/LegalServiceAuthority";
import ArbitrationMediation from "../screens/JaldiLab/Chapters/ArbitrationMediation";
import ConferenceRoom from "../screens/JaldiLab/Chapters/ConferenceRoom";
import Library from "../screens/JaldiLab/Chapters/Library";
import EvidenceRoom from "../screens/JaldiLab/Chapters/EvidenceRoom";
import SanitaryProvisions from "../screens/JaldiLab/Chapters/SanitaryProvisions";
import CanteenFacilities from "../screens/JaldiLab/Chapters/CanteenFacilities";
import FamilyAndChild from "../screens/JaldiLab/Chapters/FamilyAndChild";
import MedicalServices from "../screens/JaldiLab/Chapters/MedicalServices";
import PrayerRoom from "../screens/JaldiLab/Chapters/PrayerRoom";
import QuietRoom from "../screens/JaldiLab/Chapters/QuietRoom";
import AncillaryAmenities from "../screens/JaldiLab/Chapters/AncillaryAmenities";
import DesignAndImplementation from "../screens/JaldiLab/Chapters/DesignAndImplementation";
import SiteAndBuilding from "../screens/JaldiLab/Chapters/SiteAndBuilding";
import { Navigation } from "../screens/JaldiPortals/BuildingBetterCourts/BuildingBetterCourtsData/Navigation";
import ManagementPoliciesStrategies from "../screens/JaldiLab/Chapters/ManagementPoliciesStrategies";
import CapacityBuilding from "../screens/JaldiLab/Chapters/CapacityBuilding";
import MonitoringEvaluation from "../screens/JaldiLab/Chapters/MonitoringEvaluation";
import Glossary from "../screens/JaldiLab/Chapters/Glossary";
import AnnexureReading from "../screens/JaldiLab/AnnexureReading";

// HomePage/About/LandingScreen/BuildingBetterCourtsDetail are imported
// eagerly at the top of this file (with the rest of the file's other
// eager, non-lazy screen imports) instead of declared here with
// React.lazy() like everything else below - see the comment there for why.
const SubscribePage = React.lazy(() => import("../screens/Subscribe/SubscribePage"));
const VideoPage = React.lazy(() => import("../screens/Videos/VideoPage"));
const VideoDetailedPage = React.lazy(() => import("../screens/Videos/VideoDetailedPage"));
const ErrorPage = React.lazy(() => import("./ErrorPage"));
const AboutAuthor = React.lazy(() => import("../screens/About/AboutAuthor"));
const Events = React.lazy(() => import("../screens/Events/Events"));
const JaldiLab = React.lazy(() => import("../screens/JaldiLab/JaldiLab"));
const JaldiPortal = React.lazy(() => import("../screens/JaldiPortals/JaldiPortal"));
const AssessingCourtsCapacity = React.lazy(() =>
  import("../screens/Research/AssessingCourtsCapacity")
);
const AssessingCourtsCapacityDetail = React.lazy(() =>
  import("../screens/Research/AssessingCourtsCapacityDetail")
);
const BuildingBetterCourts = React.lazy(() => import("../screens/Research/BuildingBetterCourts"));
// BuildingBetterCourtsDetail: eager import at the top of this file - see
// the comment there.
const Engagments = React.lazy(() => import("../screens/Research/Engagments"));
const EngagmentsDetails = React.lazy(() => import("../screens/Research/EngagmentsDetails"));
const FutureProofingIndianJudiciary = React.lazy(() =>
  import("../screens/Research/FutureProofingIndianJudiciary")
);
const FutureProofingIndianJudiciaryDetail = React.lazy(() =>
  import("../screens/Research/FutureProofingIndianJudiciaryDetail")
);
const OtherPublications = React.lazy(() => import("../screens/Research/OtherPublications"));
const OtherPublicationsDetail = React.lazy(() =>
  import("../screens/Research/OtherPublicationsDetail")
);
const OverViewOfProjects = React.lazy(() => import("../screens/Research/OverViewOfProjects"));
const RepresentativeJudiciary = React.lazy(() =>
  import("../screens/Research/RepresentativeJudiciary")
);
const RepresentativeJudiciaryDetail = React.lazy(() =>
  import("../screens/Research/RepresentativeJudiciaryDetail")
);
const TechnologyDisputeRelation = React.lazy(() =>
  import("../screens/Research/TechnologyDisputeRelation")
);
const TechnologyDisputeRelationDetail = React.lazy(() =>
  import("../screens/Research/TechnologyDisputeRelationDetail")
);

const JudgeTypeDetails = React.lazy(() =>
  import("../screens/JaldiPortals/Constibench/JudgeTypeDetails")
);
const BuildingBetterCourtsData = React.lazy(() =>
  import("../screens/JaldiPortals/BuildingBetterCourts/BuildingBetterCourtsData")
);
const BuildingBetterCourtsMethod = React.lazy(() =>
  import("../screens/JaldiPortals/BuildingBetterCourts/BuildingBetterCourtsMethod")
);
const BuildingBetterDisclaimer = React.lazy(() =>
  import("../screens/JaldiPortals/BuildingBetterCourts/BuildingBetterDisclaimer")
);
const PortalBuildingBetterCourt = React.lazy(() =>
  import("../screens/JaldiPortals/BuildingBetterCourts/PortalBuildingBetterCourt")
);
const Collaborators = React.lazy(() => import("../screens/JaldiLab/Collaborators"));
const DetailedNotes = React.lazy(() => import("../screens/JaldiLab/DetailedNotes"));
const DetailedProject = React.lazy(() => import("../screens/JaldiLab/DetailedProject"));
const NotesFromField = React.lazy(() => import("../screens/JaldiLab/NotesFromField"));
const JudicialVacancy = React.lazy(() =>
  import("../screens/JaldiPortals/JudicialVacancy/JudicialVacancy")
);
const SearchAllResults = React.lazy(() => import("../components/Headers/SearchAllResults"));
const EventDetail = React.lazy(() => import("../screens/Events/EventDetail"));
const JaldiLabCourtDesignHandBook = React.lazy(() =>
  import("../screens/JaldiLab/JaldiLabCourtDesignHandBook")
);
const Projects = React.lazy(() => import("../screens/JaldiLab/Projects"));
const Archive = React.lazy(() => import("../screens/JaldiPortals/Constibench/Archive/Archive"));

const ConstitutionBenchProject = React.lazy(() =>
  import("../screens/JaldiPortals/Constibench/ConstitutionBenchProject")
);
const JudgesTypeCardDetailsList = React.lazy(() =>
  import("../screens/JaldiPortals/Constibench/JudgesTypeList")
);
const JudicialTimeTracker = React.lazy(() =>
  import("../screens/JaldiPortals/Constibench/JudicialTimeTracker/JudicialTimeTracker")
);
const JudicialBudgets = React.lazy(() =>
  import("../screens/JaldiPortals/JudicialBudgets/JudicialBudgets")
);
const RTITemplate = React.lazy(() => import("../screens/JaldiPortals/RTI/RTITemplate"));
const GenderDiversity = React.lazy(() =>
  import("../screens/JaldiPortals/WomenInJudiciary/GenderDiversity")
);
const OverallGenderDiversity = React.lazy(() =>
  import("../screens/JaldiPortals/WomenInJudiciary/OverallGenderDiversity")
);
const WomenInJudiciary = React.lazy(() =>
  import("../screens/JaldiPortals/WomenInJudiciary/WomenInJudiciary")
);
const MyWork = React.lazy(() => import("../screens/MyWork/MyWork"));
const PrivacyPoicy = React.lazy(() => import("../screens/TermsAndPolicy/PrivacyPoicy"));
const TermsAndConditions = React.lazy(() => import("../screens/TermsAndPolicy/TermsAndConditions"));

// const router = createBrowserRouter(
//   createRoutesFromElements(
//     <Route errorElement={<ErrorPage />}>
//     <Route path="/" element={<HomePage />} caseSensitive>
//     <Route index element={<LandingScreen />} />
//     <Route path="questions" element={<Questions />} />
//     <Route path="bills" element={<Bills />}  />
//     <Route path="bills/allbills" element={<AllBill />} />
//     <Route path="debates" element={<Debates />} />
//   </Route>
//   <Route path="login" element={<Login />} />
//   <Route path="signup" element={<SignUp />} />
//   </Route>
//   )
// );

const AnalyticsPageView = ({ title, children }) => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname,
      title: title || document.title,
    });

    document.title = title || document.title;
  }, [location.pathname]);

  return <>{children}</>;
};

const appRoutes = [
  {
    path: "/",
    element: (
      <React.Suspense fallback={<LoaderComponent height="80vh" />}>
        <AnalyticsPageView>
          {" "}
          <HomePage />{" "}
        </AnalyticsPageView>
      </React.Suspense>
    ),
    errorElement: (
      <React.Suspense fallback={<LoaderComponent height="90vh" />}>
        <AnalyticsPageView>
          {" "}
          <ErrorPage />{" "}
        </AnalyticsPageView>
      </React.Suspense>
    ),
    children: [
      {
        path: "/",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Home">
              {" "}
              <LandingScreen />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/about",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="About">
              {" "}
              <About />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/about/author",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="About Author">
              {" "}
              <AboutAuthor />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/overview-projects",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Research Overview">
              {" "}
              <OverViewOfProjects />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.CAPACITY_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Capacity and Performance of Justice Delivery Systems">
              {" "}
              <AssessingCourtsCapacity />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.CAPACITY_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Capacity and Performance Report">
              {" "}
              <AssessingCourtsCapacityDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },
      {
        path: CONST.TECHNOLOGY_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Technology in Dispute Resolution">
              {" "}
              <TechnologyDisputeRelation />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.TECHNOLOGY_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <TechnologyDisputeRelationDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },
      {
        path: CONST.COURT_INFRASTRUCTURE_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Court Infrastructure">
              {" "}
              <BuildingBetterCourts />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.COURT_INFRASTRUCTURE_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <BuildingBetterCourtsDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },
      {
        path: CONST.DIVERSITY_JUDICIARY_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Diversity in the Judiciary">
              {" "}
              <RepresentativeJudiciary />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.DIVERSITY_JUDICIARY_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <RepresentativeJudiciaryDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },
      {
        path: CONST.MAKING_INDIAN_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Future-Ready Judiciary">
              {" "}
              <FutureProofingIndianJudiciary />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.MAKING_INDIAN_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <FutureProofingIndianJudiciaryDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },

      {
        path: CONST.OTHER_PUBLICATION_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Other Reports">
              {" "}
              <OtherPublications />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.OTHER_PUBLICATION_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <OtherPublicationsDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.ENGAGEMENT_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Engagement">
              {" "}
              <Engagments />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.LEGACY_ENGAGEMENT_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Engagement">
              {" "}
              <Engagments />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.ENGAGEMENT_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <EngagmentsDetails />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },
      {
        path: CONST.LEGACY_ENGAGEMENT_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <EngagmentsDetails />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ), //detail Page
      },
      {
        path: "/jaldilab",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="JaldiLab">
              {" "}
              <JaldiLab />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.JALDILAB_PROJECTS_DETAIL_STATIC_PATH,
        element: (
          <AnalyticsPageView title="Court Design Handbook">
            <JaldiLabCourtDesignHandBook />
          </AnalyticsPageView>
        ),
        children: [
          {
            path: "",
            element: <IntroductionPage />,
          },
          {
            path: "mappingUserJourneys",
            element: <MappingUserPage />,
          },
          {
            path: "designPrinciples",
            element: <DesignPrincipals />,
          },
          {
            path: "spacePlanningCirculation",
            element: <SpacingPlanning />,
          },
          {
            path: "courtroom",
            element: <CourtRoomsPage />,
          },
          {
            path: "waitingArea",
            element: <WaitingAreas />,
          },
          {
            path: "vwdc",
            element: <VWDC />,
          },
          {
            path: "judgesChamber",
            element: <JudgeChamber />,
          },
          {
            path: "facilitiesForJudges",
            element: <FacilitiesForJudges />,
          },
          {
            path: "adjacenciesOfAdministrativeOffices",
            element: <AdministrativeWing />,
          },
          {
            path: "filingSection",
            element: <FilingSection />,
          },
          {
            path: "officeSpaces",
            element: <OfficeSpacesPage />,
          },
          {
            path: "RecordsOffices",
            element: <RecordsOffices />,
          },
          {
            path: "staffLounge",
            element: <StaffLounge />,
          },
          {
            path: "advocatesBarAssociation",
            element: <AdvocatesBarAssociation />,
          },
          {
            path: "chambersOfPPGP",
            element: <ChamberAssistant />,
          },
          {
            path: "judicialLockUp",
            element: <LockUp />,
          },
          {
            path: "visitationRoom",
            element: <VisitationRoom />,
          },
          {
            path: "policeInspectorsRoom",
            element: <PoliceInspectorRoom />,
          },
          {
            path: "eSevaKendra",
            element: <ESevaKendra />,
          },
          {
            path: "legalServicesAuthority",
            element: <LegalService />,
          },
          {
            path: "arbitrationAndMediationCenter",
            element: <ArbitrationMediation />,
          },
          {
            path: "conferenceMeetingRoom",
            element: <ConferenceRoom />,
          },
          {
            path: "library",
            element: <Library />,
          },
          {
            path: "evidenceRoom",
            element: <EvidenceRoom />,
          },
          {
            path: "sanitaryProvisions",
            element: <SanitaryProvisions />,
          },
          {
            path: "canteenFacilities",
            element: <CanteenFacilities />,
          },
          {
            path: "familyChildFriendlyServices",
            element: <FamilyAndChild />,
          },
          {
            path: "medicalServices",
            element: <MedicalServices />,
          },
          {
            path: "prayerRoom",
            element: <PrayerRoom />,
          },
          {
            path: "quietSensoryRoom",
            element: <QuietRoom />,
          },
          {
            path: "ancillaryAmenities",
            element: <AncillaryAmenities />,
          },
          {
            path: "designAndImplementationProcess",
            element: <DesignAndImplementation />,
          },
          {
            path: "siteBuildingManagement",
            element: <SiteAndBuilding />,
          },
          {
            path: "accessibilityNavigationPlan",
            element: <Navigation />,
          },
          {
            path: "managementPoliciesStrategies",
            element: <ManagementPoliciesStrategies />,
          },
          {
            path: "capacityBuilding",
            element: <CapacityBuilding />,
          },
          {
            path: "monitoringEvaluation",
            element: <MonitoringEvaluation />,
          },
          {
            path: "annexes",
            element: <AnnexureReading />,
          },
          {
            path: "glossary",
            element: <Glossary />,
          },
        ],
      },
      {
        path: "/jaldilab/projects",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <Projects />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.JALDILAB_PROJECTS_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <DetailedProject />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldilab/organisations",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Organisations">
              {" "}
              <Collaborators />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldilab/collaborators",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Collaborators">
              {" "}
              <Collaborators />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldilab/notes-from-field",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Notes From the Field">
              {" "}
              <NotesFromField />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: CONST.JALDILAB_NOTES_DETAIL_PATH,
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <DetailedNotes />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },

      {
        path: "/jaldi-portal",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Jaldi Portal">
              {" "}
              <JaldiPortal />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/constitution-bench",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Constitution Bench Pendency Portal">
              {" "}
              <ConstitutionBenchProject />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/judge-bench-lists/:judgeType",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Constitution Bench Cases List">
              {" "}
              <JudgesTypeCardDetailsList />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/judge-bench-details/:id/:type/:title",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Constitution Bench Case Details">
              {" "}
              <JudgeTypeDetails />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/judge-bench-details/:id/:type/:subType/:title",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Constitution Bench Case Details">
              {" "}
              <JudgeTypeDetails />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/constitution-bench-time-tracker",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Constitution Bench Time Tracker">
              {" "}
              <JudicialTimeTracker />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/constitution-bench/archive",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Archive Cases">
              {" "}
              <Archive />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      // {
      //   path: "/archived-judges-bench-details/:id/:title",
      //   element: (
      //     <React.Suspense fallback={<LoaderComponent height="90vh" />}>
      //       <AnalyticsPageView title="Archived Case Details">
      //         {" "}
      //         <ArchiveDetais />{" "}
      //       </AnalyticsPageView>
      //     </React.Suspense>
      //   ),
      // },
      {
        path: "/events",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Events">
              {" "}
              <Events />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/events/:eventType",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Events">
              {" "}
              <Events />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/events/detail",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <EventDetail />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/videos",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Videos">
              {" "}
              <VideoPage />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/videos/:id",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Videos">
              {" "}
              <VideoDetailedPage />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/RTITemplate",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="RTI Portal">
              {" "}
              <RTITemplate />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/judicial-budgets",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Judicial Budgets">
              {" "}
              <JudicialBudgets />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/women-in-judiciary/gender-diversity-portal",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Change in Gender Diversity in the District Judiciary">
              {" "}
              <GenderDiversity />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/women-in-judiciary",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Women in the Higher Judiciary">
              {" "}
              <WomenInJudiciary />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },

      {
        path: "/jaldi-portal/judicial-vacancy",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Judicial Vacancy">
              {" "}
              <JudicialVacancy />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/women-in-judiciary/overall-gender-diversity",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Overall gender diversity">
              {" "}
              <OverallGenderDiversity />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/portal-building-better-court",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Building Better Courts - About">
              {" "}
              <PortalBuildingBetterCourt />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/building-better-court-method",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Building Better Courts - Method">
              {" "}
              <BuildingBetterCourtsMethod />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/building-better-court-disclaimer",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Building Better Courts - Disclaimer">
              {" "}
              <BuildingBetterDisclaimer />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/jaldi-portal/building-better-court-data",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Building Better Courts - Data">
              {" "}
              <BuildingBetterCourtsData />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },

      {
        path: "/privacy-policy",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Privacy-Policy">
              {" "}
              <PrivacyPoicy />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/terms-conditions",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Terms and Conditions">
              {" "}
              <TermsAndConditions />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/subscribe",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView title="Subscribe">
              {" "}
              <SubscribePage />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
      {
        path: "/search",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <SearchAllResults />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },

      {
        path: "/testing-work",
        element: (
          <React.Suspense fallback={<LoaderComponent height="90vh" />}>
            <AnalyticsPageView>
              {" "}
              <MyWork />{" "}
            </AnalyticsPageView>
          </React.Suspense>
        ),
      },
    ],
  },
];

const applyCmsOverrides = (routes) =>
  routes.map((route) => {
    const children = route.children ? applyCmsOverrides(route.children) : route.children;
    const isLeafRoute = !children || children.length === 0;

    return {
      ...route,
      children,
      element:
        isLeafRoute && route.element ? (
          <CmsPageOverride>{route.element}</CmsPageOverride>
        ) : (
          route.element
        ),
    };
  });

const router = createBrowserRouter(applyCmsOverrides(appRoutes));

const MyRoutes = () => {
  return <RouterProvider router={router} />;
};

export default MyRoutes;
